import { useState } from 'react';
import { get, post } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatCents } from '../../shared/money';
import { formatDateTime } from '../../shared/format';
import { Badge, Button, Empty, Field, Money, MoneyInput, Sheet, Spinner, useAction, useToast, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { storeToday, useDailyReportPrint } from './DailyReport';
import { useCloudPrint } from '../lib/printing';

const KIND: Record<string, string> = { sale: 'Cash sales', refund: 'Cash refunds', paid_in: 'Cash added', paid_out: 'Cash removed', no_sale: 'No-sale opens', buyback: 'Cash buybacks', expense: 'Expenses', wholesale: 'Wholesale (cash)' };

export function OpenDrawerSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [cents, setCents] = useState<number | null>(null);
  const { run, busy } = useAction();
  const open = async () => {
    const r = await run(() => post('/api/drawer/open', { openingCents: cents ?? 0 }), { success: 'Drawer open. Ready to sell.' });
    if (r) onDone();
  };
  return (
    <Sheet title="Open the cash drawer" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={open} disabled={cents === null}>Open drawer</Button></>}>
      <p className="muted">Count the cash in the drawer and enter the total.</p>
      <Field label="Starting cash"><MoneyInput value={cents} onChange={setCents} autoFocus /></Field>
    </Sheet>
  );
}

function MoveSheet({ kind, onClose, onDone }: { kind: 'paid_in' | 'paid_out' | 'no_sale'; onClose: () => void; onDone: () => void }) {
  const [cents, setCents] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const { run, busy } = useAction();
  // The drawer is plugged into the receipt printer: with PrintNode it opens by itself when this is saved.
  const opens = !!useCloudPrint().status?.open_drawer;
  const title = kind === 'paid_in' ? 'Add cash' : kind === 'paid_out' ? 'Remove cash' : 'Open drawer (no sale)';
  const go = async () => {
    const r = await run((approval?: ApprovalInput) => post('/api/drawer/move', { kind, amountCents: cents ?? undefined, note, approval }), { success: opens ? 'Saved. The drawer is opening.' : 'Saved.' });
    if (r) onDone();
  };
  return (
    <Sheet title={title} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={go} disabled={kind !== 'no_sale' && (!cents || !note.trim())}>{kind === 'no_sale' && opens ? 'Open drawer' : 'Save'}</Button></>}>
      {kind !== 'no_sale' ? <Field label="Amount"><MoneyInput value={cents} onChange={setCents} autoFocus /></Field> : <p className="muted">This is recorded with your name. A manager may need to approve it.{opens ? ' The drawer opens by itself.' : ''}</p>}
      <Field label={kind === 'no_sale' ? 'Reason (optional)' : 'Reason'}><input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === 'paid_out' ? 'e.g. Bank deposit, supplies' : 'e.g. Change from the safe'} /></Field>
    </Sheet>
  );
}

function CloseSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { me } = useSession();
  const [cents, setCents] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<any>(null);
  const { run, busy } = useAction();
  const printReport = useDailyReportPrint();
  const today = storeToday(me?.settings?.timezone);
  const go = async () => {
    const r = await run(() => post('/api/drawer/close', { countedCents: cents ?? 0, note: note || undefined }));
    if (r) {
      setResult(r);
      // The end of the day: the daily close-out report prints by itself (the day the shift opened,
      // even when it's closed after midnight).
      printReport(r.day ?? today).catch(() => undefined);
    }
  };
  if (result) {
    const d = result.difference_cents;
    return (
      <Sheet title="Drawer closed" onClose={onDone} footer={<><Button icon="printer" onClick={() => { printReport(result.day ?? today).catch(() => undefined); }}>Print daily report</Button><Button variant="primary" onClick={onDone}>Done</Button></>}>
        <div className="stat-row">
          <div className="stat"><div className="k">You counted</div><div className="v">{formatCents(result.counted_cents)}</div></div>
          <div className="stat"><div className="k">Expected</div><div className="v">{formatCents(result.expected_cents)}</div></div>
          <div className="stat" style={{ background: d === 0 ? 'var(--green-soft)' : 'var(--amber-soft)' }}><div className="k">Difference</div><div className="v">{d === 0 ? 'Balanced' : `${d > 0 ? 'Over' : 'Short'} ${formatCents(Math.abs(d))}`}</div></div>
        </div>
        {d !== 0 ? <div className="banner warn"><Icon.alert /><div>A manager will review this difference.</div></div> : <div className="banner"><Icon.check /><div>Perfect count. Thanks!</div></div>}
        <p className="small muted">Today’s close-out report is printing: cash and card, repairs, parts ordered, activations, bill payments and who closed.</p>
      </Sheet>
    );
  }
  return (
    <Sheet title="Close the drawer" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={go} disabled={cents === null}>Close drawer</Button></>}>
      <p className="muted">Count all the cash in the drawer and enter the total. You'll see if it matches after you close.</p>
      <Field label="Cash counted"><MoneyInput value={cents} onChange={setCents} autoFocus /></Field>
      <Field label="Note (optional)"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
    </Sheet>
  );
}

export function DrawerScreen() {
  const { me, can } = useSession();
  const cur = useLoad(() => (me?.register ? get('/api/drawer') : Promise.resolve(null)), [me?.register?.id]);
  const shifts = useLoad(() => (can('drawer.review') ? get('/api/drawer/shifts') : Promise.resolve([])), []);
  const [sheet, setSheet] = useState<null | 'open' | 'close' | 'paid_in' | 'paid_out' | 'no_sale'>(null);
  const [review, setReview] = useState<any>(null);
  const done = () => { setSheet(null); cur.reload(); shifts.reload(); };
  const shift = cur.data?.shift;
  return (
    <div className="page">
      <div className="page-head"><h1>Cash drawer</h1></div>
      {!me?.register ? (
        <div className="banner warn"><Icon.alert /><div>The cash drawer belongs to a store tablet. Open STORE OS on the tablet to open or close it.</div></div>
      ) : cur.loading && !cur.data ? (
        <Spinner />
      ) : shift ? (
        <div className="card stack">
          <div className="spread">
            <div>
              <h2>Drawer is open</h2>
              <p className="muted">{shift.register_name} · opened by {shift.opened_by_name} at {formatDateTime(shift.opened_at)}</p>
            </div>
            <Badge tone="green" icon="check">Open</Badge>
          </div>
          <div className="stat-row">
            <div className="stat"><div className="k">Starting cash</div><div className="v">{formatCents(shift.opening_cents)}</div></div>
            {shift.expected_cents !== null ? <div className="stat"><div className="k">Expected in drawer now</div><div className="v">{formatCents(shift.expected_cents)}</div></div> : null}
            {(shift.summary ?? []).map((s: any) => (
              <div key={s.kind} className="stat"><div className="k">{KIND[s.kind] ?? s.kind} ({s.n})</div><div className="v">{formatCents(s.total)}</div></div>
            ))}
          </div>
          <div className="row-wrap">
            <Button icon="plus" onClick={() => setSheet('paid_in')}>Add cash</Button>
            <Button icon="minus" onClick={() => setSheet('paid_out')}>Remove cash</Button>
            <Button icon="unlock" onClick={() => setSheet('no_sale')}>No sale</Button>
            <span className="grow" />
            <Button variant="primary" size="lg" icon="lock" onClick={() => setSheet('close')}>Close drawer</Button>
          </div>
        </div>
      ) : (
        <div className="card stack">
          <h2>Drawer is closed</h2>
          <p className="muted">Open it at the start of the shift. Count the cash and enter the total.</p>
          <div><Button variant="primary" size="lg" icon="cash" onClick={() => setSheet('open')}>Open drawer</Button></div>
        </div>
      )}

      {can('drawer.review') ? (
        <section className="stack">
          <h2>Recent drawer counts</h2>
          {shifts.data?.length ? (
            <div className="list">
              <table className="table">
                <thead><tr><th>Closed</th><th>Tablet</th><th>By</th><th className="right">Counted</th><th className="right">Expected</th><th className="right">Difference</th><th>Status</th></tr></thead>
                <tbody>
                  {shifts.data.map((s: any) => (
                    <tr key={s.id} className="click" onClick={() => s.status !== 'open' && setReview(s)}>
                      <td>{s.closed_at ? formatDateTime(s.closed_at) : 'Still open'}</td>
                      <td>{s.location_name} · {s.register_name}</td>
                      <td>{s.closed_by_name ?? s.opened_by_name}</td>
                      <td className="right"><Money cents={s.counted_cents} /></td>
                      <td className="right"><Money cents={s.expected_cents} /></td>
                      <td className="right">{s.difference_cents === null ? '—' : s.difference_cents === 0 ? <Badge tone="green">Balanced</Badge> : <Badge tone={s.difference_cents < 0 ? 'red' : 'amber'}>{s.difference_cents > 0 ? 'Over' : 'Short'} {formatCents(Math.abs(s.difference_cents))}</Badge>}</td>
                      <td>{s.status === 'reviewed' ? <Badge tone="blue" icon="check">Reviewed</Badge> : s.status === 'open' ? <Badge>Open</Badge> : s.difference_cents ? <Badge tone="amber" icon="alert">Needs review</Badge> : <Badge>Closed</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty icon="cash" title="No drawer counts yet." />
          )}
        </section>
      ) : null}

      {sheet === 'open' ? <OpenDrawerSheet onClose={() => setSheet(null)} onDone={done} /> : null}
      {sheet === 'close' ? <CloseSheet onClose={() => setSheet(null)} onDone={done} /> : null}
      {sheet === 'paid_in' || sheet === 'paid_out' || sheet === 'no_sale' ? <MoveSheet kind={sheet} onClose={() => setSheet(null)} onDone={done} /> : null}
      {review ? <ReviewSheet shift={review} onClose={() => setReview(null)} onDone={() => { setReview(null); shifts.reload(); }} /> : null}
    </div>
  );
}

function ReviewSheet({ shift, onClose, onDone }: { shift: any; onClose: () => void; onDone: () => void }) {
  const detail = useLoad(() => get(`/api/drawer/shifts/${shift.id}`), [shift.id]);
  const [note, setNote] = useState(shift.review_note ?? '');
  const { run, busy } = useAction();
  const toast = useToast();
  const save = async () => {
    const r = await run(() => post(`/api/drawer/shifts/${shift.id}/review`, { note }));
    if (r) { toast('Marked as reviewed.', 'good'); onDone(); }
  };
  return (
    <Sheet title={`Drawer count · ${shift.location_name}`} onClose={onClose} wide footer={shift.status !== 'reviewed' ? <><Button onClick={onClose}>Close</Button><Button variant="primary" busy={busy} disabled={!note.trim()} onClick={save}>Mark reviewed</Button></> : <Button onClick={onClose}>Close</Button>}>
      <div className="stat-row">
        <div className="stat"><div className="k">Counted</div><div className="v">{formatCents(shift.counted_cents ?? 0)}</div></div>
        <div className="stat"><div className="k">Expected</div><div className="v">{formatCents(shift.expected_cents ?? 0)}</div></div>
        <div className="stat"><div className="k">Difference</div><div className="v">{formatCents(shift.difference_cents ?? 0)}</div></div>
      </div>
      {detail.data?.movements?.length ? (
        <div className="list">
          <table className="table">
            <thead><tr><th>Time</th><th>What</th><th>By</th><th className="right">Amount</th></tr></thead>
            <tbody>
              {detail.data.movements.map((m: any) => (
                <tr key={m.id}>
                  <td>{formatDateTime(m.created_at)}</td>
                  <td>{KIND[m.kind] ?? m.kind}{m.receipt_no ? ` · #${m.receipt_no}` : ''}{m.note ? ` · ${m.note}` : ''}{m.approved_by_name ? ` (approved by ${m.approved_by_name})` : ''}</td>
                  <td>{m.employee_name}</td>
                  <td className="right"><Money cents={m.amount_cents} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {shift.status === 'reviewed' ? <div className="banner"><Icon.check /><div>Reviewed by {shift.reviewed_by_name}: {shift.review_note}</div></div> : (
        <Field label="What did you find?"><textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Recounted with the employee; $5 was given as extra change." /></Field>
      )}
    </Sheet>
  );
}
